import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import MessageComposer from '../components/MessageComposer';
import { User, SendResult, BroadcastResult, WelcomeMessageConfig } from '../types';

export default function UserList() {
  const [users, setUsers] = useState<User[]>([]);
  const [selected, setSelected] = useState<(string | number)[]>([]);
  const [results, setResults] = useState<SendResult | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState<BroadcastResult | null>(null);
  const [broadcastPolling, setBroadcastPolling] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [welcomeStatus, setWelcomeStatus] = useState<WelcomeMessageConfig | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('page_size', String(pageSize));
      if (searchQuery) params.set('q', searchQuery);

      const res = await fetch(`/api/users?${params.toString()}`);
      const data = await res.json();

      if (Array.isArray(data)) {
        setUsers(data);
        setTotalPages(1);
        setTotalCount(data.length);
      } else if (data.data) {
        setUsers(data.data);
        setTotalPages(data.total_pages || Math.ceil((data.total || data.data.length) / pageSize));
        setTotalCount(data.total || data.data.length);
      } else if (data.users) {
        setUsers(data.users);
        setTotalPages(data.total_pages || Math.ceil((data.total || data.users.length) / pageSize));
        setTotalCount(data.total || data.users.length);
      } else {
        setUsers([]);
        setTotalPages(1);
        setTotalCount(0);
      }
    } catch {
      setUsers([]);
    }
    setLoadingUsers(false);
  }, [page, pageSize, searchQuery]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setSearchQuery(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  useEffect(() => {
    fetch('/api/welcome/settings')
      .then(r => r.json())
      .then(data => setWelcomeStatus(data))
      .catch(() => {});
  }, []);

  const getUserId = (user: User): string | number => user.user_id || user.chat_id || user.id || 0;
  const getUserName = (user: User): string => user.username || user.name || user.first_name || String(getUserId(user));
  const getUserDept = (user: User): string => user.department || user.group || '';

  const toggleSelect = (userId: string | number) => {
    setSelected(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const toggleAll = () => {
    if (selected.length === users.length) {
      setSelected([]);
    } else {
      setSelected(users.map(u => getUserId(u)));
    }
  };

  const pollBroadcastStatus = (jobId: string) => {
    setBroadcastPolling(true);
    pollingRef.current = setInterval(async () => {
      try {
        const res = await fetch(`/api/telegram/broadcast/status?job_id=${encodeURIComponent(jobId)}`);
        const data = await res.json();
        setBroadcastResult(prev => ({ ...prev, status: data }));

        if (data.status === 'completed' || data.status === 'failed' || data.error) {
          clearInterval(pollingRef.current!);
          pollingRef.current = null;
          setBroadcastPolling(false);
        }
      } catch {
        clearInterval(pollingRef.current!);
        pollingRef.current = null;
        setBroadcastPolling(false);
      }
    }, 2000);
  };

  const handleBroadcastResult = (data: BroadcastResult) => {
    setBroadcastResult(data);
    if (data && data.job_id) {
      pollBroadcastStatus(data.job_id);
    }
  };

  return (
    <div>
      <h2 style={{ marginBottom: '20px' }}>사용자 목록</h2>

      {/* 환영 메시지 상태 배너 */}
      {welcomeStatus && (
        <div style={{
          marginBottom: '12px', padding: '10px 16px', borderRadius: '6px', fontSize: '13px',
          display: 'flex', alignItems: 'center', gap: '8px',
          background: welcomeStatus.enabled ? '#e8f5e9' : '#f5f5f5',
          border: `1px solid ${welcomeStatus.enabled ? '#a5d6a7' : '#e0e0e0'}`,
        }}>
          <span style={{
            display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%',
            background: welcomeStatus.enabled ? '#4caf50' : '#bdbdbd',
          }} />
          {welcomeStatus.enabled
            ? <span style={{ color: '#2e7d32' }}>환영 메시지 자동 전송 활성화됨</span>
            : <span style={{ color: '#888' }}>환영 메시지 미설정 — <Link to="/settings" style={{ color: '#1976d2' }}>설정하기</Link></span>
          }
        </div>
      )}

      <div style={{ marginBottom: '12px' }}>
        <input
          type="text"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          placeholder="사용자 검색..."
          style={{
            width: '100%', padding: '10px 12px', border: '1px solid #ccc',
            borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box',
          }}
        />
      </div>

      <div style={{ background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f9f9f9', borderBottom: '2px solid #e0e0e0' }}>
              <th style={{ padding: '12px', textAlign: 'center', width: '50px' }}>
                <input type="checkbox" checked={users.length > 0 && selected.length === users.length} onChange={toggleAll} />
              </th>
              <th style={{ padding: '12px', textAlign: 'left' }}>이름</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>부서/그룹</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>User ID</th>
            </tr>
          </thead>
          <tbody>
            {loadingUsers ? (
              <tr><td colSpan={4} style={{ padding: '20px', textAlign: 'center', color: '#888' }}>로딩 중...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={4} style={{ padding: '20px', textAlign: 'center', color: '#888' }}>사용자가 없습니다.</td></tr>
            ) : users.map(user => {
              const uid = getUserId(user);
              return (
                <tr key={String(uid)} onClick={() => toggleSelect(uid)}
                  style={{ borderBottom: '1px solid #eee', cursor: 'pointer', background: selected.includes(uid) ? '#e3f2fd' : '#fff' }}>
                  <td style={{ padding: '12px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.includes(uid)} onChange={() => toggleSelect(uid)} />
                  </td>
                  <td style={{ padding: '12px' }}>{getUserName(user)}</td>
                  <td style={{ padding: '12px' }}>{getUserDept(user)}</td>
                  <td style={{ padding: '12px', color: '#888', fontSize: '13px' }}>{uid}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
            style={{ padding: '6px 16px', border: '1px solid #ccc', borderRadius: '6px', background: page <= 1 ? '#f0f0f0' : '#fff', cursor: page <= 1 ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
            이전
          </button>
          <span style={{ fontSize: '13px', color: '#666' }}>{page} / {totalPages} 페이지 (총 {totalCount}명)</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
            style={{ padding: '6px 16px', border: '1px solid #ccc', borderRadius: '6px', background: page >= totalPages ? '#f0f0f0' : '#fff', cursor: page >= totalPages ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
            다음
          </button>
        </div>
      )}

      <div style={{ marginTop: '24px', background: '#fff', padding: '24px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h3 style={{ marginBottom: '16px' }}>
          메시지 전송
          {selected.length > 0 && <span style={{ fontSize: '14px', color: '#1976d2', fontWeight: 400, marginLeft: '8px' }}>({selected.length}명 선택)</span>}
        </h3>
        <MessageComposer
          selected={selected}
          users={users}
          getUserId={getUserId}
          getUserName={getUserName}
          onResult={setResults}
          onBroadcastResult={handleBroadcastResult}
        />
      </div>

      {results && (
        <div style={{ marginTop: '16px', background: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h4 style={{ marginBottom: '12px' }}>전송 결과</h4>
          {results.error ? (
            <p style={{ color: '#d32f2f' }}>{results.error}</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {results.results.map((r, i) => {
                const user = users.find(u => String(getUserId(u)) === String(r.userId));
                const errorMsg = !r.success && r.detail
                  ? (typeof r.detail === 'string' ? r.detail : ((r.detail as Record<string, string>).message || (r.detail as Record<string, string>).error || JSON.stringify(r.detail)))
                  : null;
                return (
                  <li key={i} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', background: r.success ? '#4caf50' : '#d32f2f' }} />
                      <span>{user ? getUserName(user) : r.userId}</span>
                      <span style={{ color: r.success ? '#4caf50' : '#d32f2f', fontSize: '13px' }}>{r.success ? '성공' : '실패'}</span>
                    </div>
                    {errorMsg && (
                      <div style={{ marginTop: '4px', marginLeft: '18px', padding: '6px 10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px', fontSize: '12px', color: '#991b1b', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {errorMsg}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {broadcastResult && (
        <div style={{ marginTop: '16px', background: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h4 style={{ marginBottom: '12px' }}>일괄 전송 결과</h4>
          {broadcastResult.error ? (
            <p style={{ color: '#d32f2f' }}>{broadcastResult.error}</p>
          ) : (
            <div>
              {broadcastResult.job_id && <p style={{ fontSize: '13px', color: '#666' }}>Job ID: {broadcastResult.job_id}</p>}
              {broadcastPolling && <div style={{ color: '#1976d2', fontSize: '13px' }}>진행 중...</div>}
              {broadcastResult.status && (
                <div style={{
                  marginTop: '8px', padding: '10px 14px', borderRadius: '6px',
                  background: broadcastResult.status.status === 'completed' ? '#e8f5e9' : broadcastResult.status.status === 'failed' ? '#fef2f2' : '#fff8e1',
                  border: `1px solid ${broadcastResult.status.status === 'completed' ? '#a5d6a7' : broadcastResult.status.status === 'failed' ? '#fecaca' : '#ffe082'}`,
                }}>
                  <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '4px' }}>상태: {broadcastResult.status.status || '확인 중'}</div>
                  {broadcastResult.status.sent !== undefined && <div style={{ fontSize: '13px', color: '#666' }}>전송: {broadcastResult.status.sent} / {broadcastResult.status.total || '?'}</div>}
                  {broadcastResult.status.failed !== undefined && broadcastResult.status.failed > 0 && <div style={{ fontSize: '13px', color: '#d32f2f' }}>실패: {broadcastResult.status.failed}</div>}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
